import { integrationTypeDisplayName } from '@commenda/api/sales-tax/types';
import {
  Button,
  Card,
  Icons,
  Input,
  LoadingIndicator,
} from '@commenda/kakashi';
import cn from '@commenda/utils/cn';
import { INTEGRATION_LOGOS } from '@commenda/utils/constants';
import { RootFiIntegration } from '@prisma/client';
import { useMemo, useRef, useState } from 'react';

import { useScrollGradient } from '../../hooks/useScrollGradient';
import { rootFiCategoryForIntegration } from '../helpers/RootFiInviteLinkConnectModalSyncForm';

interface Props {
  isDefaultsLoading: boolean;
  isSubmitting: boolean;
  selectedIntegration: RootFiIntegration | null;
  integrationChoicesForPicker: RootFiIntegration[];
  onSelectedIntegrationChange: (integration: RootFiIntegration | null) => void;
  showSelectedIntegration?: boolean;
}

const formatIntegrationCategoryLabel = (
  integration: RootFiIntegration
): string => rootFiCategoryForIntegration(integration).toLowerCase();

const RootFiInviteLinkConnectModalIntegrationPicker = ({
  isDefaultsLoading,
  isSubmitting,
  selectedIntegration,
  integrationChoicesForPicker,
  onSelectedIntegrationChange,
  showSelectedIntegration = true,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const filteredIntegrations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return integrationChoicesForPicker;

    return integrationChoicesForPicker.filter((integration) =>
      integrationTypeDisplayName(integration).toLowerCase().includes(query)
    );
  }, [integrationChoicesForPicker, searchQuery]);

  const { showTopGradient, showBottomGradient } = useScrollGradient(
    scrollRef,
    `${filteredIntegrations.length}-${searchQuery}`
  );

  const renderIntegrationCard = (
    integration: RootFiIntegration,
    options: { isSelected: boolean; onSelect?: () => void }
  ) => (
    <Card.Root
      key={integration}
      _className={cn(
        'flex-row shrink-0 items-center justify-between gap-lg px-lg py-lg transition-colors',
        options.onSelect && 'cursor-pointer hover:bg-bg-primary-hover',
        options.isSelected && 'border border-border-primary'
      )}
      onClick={options.onSelect}
    >
      <div className="flex items-center gap-lg">
        <img
          src={INTEGRATION_LOGOS[integration]}
          alt=""
          className="size-10 shrink-0"
        />
        <div className="flex min-w-0 flex-col">
          <p className="txt-small-bold text-text-primary">
            {integrationTypeDisplayName(integration)}
          </p>
          <p className="txt-extra-small-regular capitalize text-text-secondary">
            {formatIntegrationCategoryLabel(integration)}
          </p>
        </div>
      </div>
      {options.isSelected ? (
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            setSearchQuery('');
            onSelectedIntegrationChange(null);
          }}
        >
          Change
        </Button>
      ) : null}
    </Card.Root>
  );

  return (
    <div className="flex flex-col gap-md px-3xl py-xl">
      {isDefaultsLoading || isSubmitting ? (
        <div className="flex justify-center py-xl">
          <LoadingIndicator />
        </div>
      ) : !selectedIntegration || !showSelectedIntegration ? (
        <>
          <Input
            placeholder="Search integration"
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="h-11 border-border-primary"
            leftEl={() => (
              <Icons.SearchMd className="size-4 text-text-quaternary" />
            )}
          />
          <div className="relative">
            <div
              ref={scrollRef}
              className="max-h-[440px] overflow-y-auto overscroll-contain"
            >
              <div className="flex flex-col gap-md">
                {filteredIntegrations.length > 0 ? (
                  filteredIntegrations.map((integration) =>
                    renderIntegrationCard(integration, {
                      isSelected: false,
                      onSelect: () => onSelectedIntegrationChange(integration),
                    })
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center gap-xxs py-xl">
                    <p className="txt-medium-bold text-text-primary">
                      No integration found
                    </p>
                    <p className="txt-small-regular text-text-tertiary">
                      Try searching with a different keyword
                    </p>
                  </div>
                )}
              </div>
            </div>
            {showTopGradient ? (
              <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-12 bg-gradient-to-b from-[var(--color-bg-primary)] to-transparent" />
            ) : null}
            {showBottomGradient ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-12 bg-gradient-to-t from-[var(--color-bg-primary)] to-transparent" />
            ) : null}
          </div>
        </>
      ) : (
        renderIntegrationCard(selectedIntegration, { isSelected: true })
      )}
    </div>
  );
};

export default RootFiInviteLinkConnectModalIntegrationPicker;
